import { Loading } from '@ui-components/neo.tax';
import { useEffect, useState } from 'react';

import { useRdContext } from '@neo.tax/rd/context';
import { useRdExpenseProjectAllocations, useRdExpense, type RdProject } from '@neo.tax/rd/hooks';

import { EditQreExpenseAllocationModal } from './EditQreExpenseAllocationModal';
import { StyledModal } from './qre-allocation.styled';

type EditQreExpenseAllocationModalLoaderProps = {
  rdExpenseId: string;
  close: () => void;
  isOpen: boolean;
  selectedRdProject?: RdProject;
};

export const EditQreExpenseAllocationModalLoader = ({
  close,
  isOpen,
  rdExpenseId,
  selectedRdProject,
}: EditQreExpenseAllocationModalLoaderProps): React.ReactElement | null => {
  const skip = !isOpen;
  const [searchKeyword, setSearchKeyword] = useState<string | null>(null);

  // Reset search keyword when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSearchKeyword(null);
    }
  }, [isOpen]);

  const { fiscalYearId } = useRdContext();

  const { loading: loadingRdExpense, rdExpense } = useRdExpense(rdExpenseId, { skip });

  // Fetch all project allocations (without search filter) for allocation calculations
  const {
    loading: loadingAllRdExpenseProjectAllocations,
    rdExpenseProjectAllocations: allRdExpenseProjectAllocations,
  } = useRdExpenseProjectAllocations({
    skip,
    rdExpenseId,
    fiscalYearId,
    debounceKey: 'all-rd-expense-project-allocations',
  });

  // Fetch filtered project allocations for UI display
  const {
    loading: loadingFilteredRdExpenseProjectAllocations,
    rdExpenseProjectAllocations: filteredRdExpenseProjectAllocations,
  } = useRdExpenseProjectAllocations({
    skip,
    rdExpenseId,
    fiscalYearId,
    searchKeyword,
    debounceKey: 'filtered-rd-expense-project-allocations',
  });

  if (loadingRdExpense || !rdExpense) {
    return (
      <StyledModal isOpen={isOpen} setIsOpen={close}>
        <Loading height={120} />
      </StyledModal>
    );
  }
  return (
    <EditQreExpenseAllocationModal
      isOpen={isOpen}
      close={close}
      filteredRdExpenseProjectAllocations={filteredRdExpenseProjectAllocations}
      filteredRdExpenseProjectAllocationsLoading={loadingFilteredRdExpenseProjectAllocations}
      allRdExpenseProjectAllocations={allRdExpenseProjectAllocations}
      allRdExpenseProjectAllocationsLoading={loadingAllRdExpenseProjectAllocations}
      rdExpense={rdExpense}
      selectedRdProject={selectedRdProject}
      searchKeyword={searchKeyword}
      setSearchKeyword={setSearchKeyword}
    />
  );
};
